import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import {
	ArchivePolicyIcon,
	ArrowDownIcon,
	ChatIcon,
	EmailIcon,
	HeadphonesIcon,
	HomeIcon,
	LogoutIcon,
	SfClockArrowCirclepathIcon,
	TelephoneIcon,
	UserIcon,
} from '@ninja/icons/web';
import { useLocale } from '@ninja/nextjs';
import { INinjaThemeProvider, LayoutHeaderMenu } from '@ninja/web';
import { AppConfig } from '../config/app-config';
import { useWebNavigation } from '../contexts/navigation';
import { useWebUser } from './useWebUser';

const useLayoutSetting = ({
	liveChat,
}: {
	liveChat: {
		startChat: () => void;
		isChatReady: boolean;
		logoutChatClient: () => void;
	};
}) => {
	const { locale, setLocaleLang, isRtlLocale, toggleLanguage } = useLocale();
	const { navigationPush } = useWebNavigation();
	const { isUserLoggedIn, userName, signOut } = useWebUser();
	const { t } = useTranslation();
	const logout = async () => {
		await signOut({ redirect: true, callbackUrl: '/' });
		liveChat.logoutChatClient();
	};
	const [firstName] = userName?.split(' ') ?? '';

	const currentUser: INinjaThemeProvider['currentUser'] = {
		displayName: firstName,
		isLoggedIn: isUserLoggedIn,
		showAvatarAndDisplayName: true,
		userMenuItems: isUserLoggedIn
			? [
					{
						label: t('shared.user-setting-menu.dashboard'),
						icon: HomeIcon,
						onClick: () => navigationPush('Dashboard'),
						link: {
							href: AppConfig.dashboardUrl,
						},
					},
					{
						label: t('shared.user-setting-menu.my-policies'),
						icon: ArchivePolicyIcon,
						onClick: () => navigationPush('MyPolicies'),
						link: {
							href: AppConfig.myPoliciesUrl,
						},
					},
					{
						label: t('shared.user-setting-menu.account-settings'),
						icon: UserIcon,
						onClick: () => navigationPush('AccountSettings'),
						link: {
							href: AppConfig.accountSettingsUrl,
						},
					},
					{
						label: t('shared.user-setting-menu.notification-history'),
						icon: SfClockArrowCirclepathIcon,
						onClick: () => navigationPush('NotificationHistory'),
						link: {
							href: AppConfig.notificationHistoryUrl,
						},
					},
					{
						label: 'EN /عربي',
						className: 'font-medium ',
						onClick: toggleLanguage,
					},
					{
						label: t('shared.user-setting-menu.logout'),
						className: '!text-red-500 ',
						icon: LogoutIcon,
						onClick: logout,
					},
			  ]
			: [
					{
						label: t('shared.user-setting-menu.login'),
						onClick: () => navigationPush('Login', { src: window.location.href }),
					},
			  ],
	};

	const prepareIcon = (isOpen?: boolean) => (
		<div className="flex gap-2 items-center">
			<HeadphonesIcon className="w-6 h-6" />
			<span className="text-paragraph">{t('shared.support')}</span>
			<ArrowDownIcon className={`w-3 h-2 ${isOpen ? 'rotate-180' : ''}`} />
		</div>
	);

	const settings: INinjaThemeProvider = {
		locale,
		onLanguageSwitchClick: (locale) => setLocaleLang(locale),
		logoRender: ({ logoColorMode }) => (
			<Link href="/">
				{/* eslint-disable-next-line @next/next/no-img-element */}
				<img
					style={{ height: '30px', width: '97px' }}
					src={logoColorMode === 'white' ? 'images/logo_white.svg' : 'images/logo.svg'}
					alt="Shory logo"
				/>
			</Link>
		),
		renderLink: ({ href, children, title, ...rest }) => (
			<Link href={href ?? '#'} {...rest}>
				{title ?? children}
			</Link>
		),
		currentUser,
		toastDefaults: {
			position: 'top-center',
			duration: 4000,
			isClosable: true,
		},
		drawer: {
			sections: [
				{
					items: [
						{
							label: t('shared.nav-menu.dashboard'),
							icon: HomeIcon,
							onClick: () => navigationPush('Dashboard'),
							isHidden: !isUserLoggedIn,
						},
						{
							label: t('shared.user-setting-menu.my-policies'),
							icon: ArchivePolicyIcon,
							onClick: () => navigationPush('MyPolicies'),
							isHidden: !isUserLoggedIn,
						},
						{
							label: t('shared.user-setting-menu.account-settings'),
							icon: UserIcon,
							onClick: () => navigationPush('AccountSettings'),
							isHidden: !isUserLoggedIn,
						},
						{
							label: t('shared.user-setting-menu.notification-history'),
							icon: SfClockArrowCirclepathIcon,
							onClick: () => navigationPush('NotificationHistory'),
							isHidden: !isUserLoggedIn,
						},
						{
							label: t('shared.contact-number'),
							icon: TelephoneIcon,
							onClick: () => (window.location.href = 'tel:80074679'),
							dir: 'ltr',
						},
						{
							label: t('shared.contact-email'),
							icon: EmailIcon,
							className: 'w-fit',
							onClick: () => (window.location.href = 'mailto:support@shory.com'),
						},
						{
							type: 'languageSwitch',
						},
						{
							label: t('shared.user-setting-menu.login'),
							icon: LogoutIcon,
							onClick: () => navigationPush('Login', { src: window.location.href }),
							isHidden: isUserLoggedIn,
						},
						{
							label: t('shared.user-setting-menu.logout'),
							icon: LogoutIcon,
							className: 'text-red-500',
							onClick: logout,
							isHidden: !isUserLoggedIn,
						},
					],
				},
			],
		},
		headerOptions: {
			headerMenuRender() {
				return (
					<LayoutHeaderMenu
						triggerComponent={({ isOpen }) => prepareIcon(isOpen)}
						menuItems={[
							{
								isDisabled: !liveChat.isChatReady,
								content: t('shared.contact-chat'),
								icon: <ChatIcon className="w-6 h-6" />,
								onClick: () => {
									liveChat?.startChat();
								},
							},
							{
								content: t('shared.contact-email'),
								icon: <EmailIcon className="w-6 h-6" />,
								link: { href: 'mailto:support@shory.com', isExternal: true },
							},
							{
								content: (
									<div className="flex flex-col">
										<span className={`text-paragraph ${isRtlLocale ? 'text-end' : ''}`} dir={isRtlLocale ? 'ltr' : ''}>
											{t('shared.contact-number')}
										</span>
										<span className="text-caption2 max-w-56 whitespace-break-spaces">{t('shared.contact-time')}</span>
									</div>
								),
								icon: <TelephoneIcon className="w-6 h-6" />,
								link: { href: 'tel:80074679', isExternal: true },
							},
						]}
					/>
				);
			},
		},
	};

	return settings;
};

export default useLayoutSetting;
